import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SimpleSearch } from '../../../../../reduxToolkit/Search/SearchApi';
import {LinkingMode, OpenTheDoor} from '../../../../../reduxToolkit/HID/HidApi';
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import TableFunction from "../../../../../components/table/TableFunction";
import { permissionObj } from "../../../../../Helpers/permission";
import {
    DEFAULT_LEFT,
    LEFT_FIRST_COLUMN_WITH_CHECKBOX,
    SEARCH_MODEL
} from "../../../../../components/table/Model/TableModel";
import { TABLES } from "../../../../../Apis/Tables";
import { toast } from "react-toastify";
import {EMPTY_VALUES, errorMsg} from "../../../../../Apis/functions";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import {
    DetailsIconsOptions
} from "../../../../../components/buttons/IconButtons";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";
import CustomTooltip from '../../../../../components/CustomTooltip';

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * Main ACR table management component. Handles the rendering, pagination,
 * deletion, and data retrieval of Access Control Reader (ACR) records.
 *
 * It integrates with Redux Toolkit APIs for server-side pagination and deletion,
 * and leverages custom table components for consistent UI behavior.
 *
 * @param {Array} selectedList - List of currently selected ACR records.
 * @param {Function} setSelectedList - Function to update the selected record list.
 *
 * @returns {Object} - Returns table handlers, configuration, and UI control references.
 */
const MainAcrTableApp = ({ selectedList, setSelectedList }) => {
    const dispatch = useDispatch();
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [search, setSearch] = useState(false);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [searchElement, setSearchElement] = useState('');
    const [bodyForExport, setBodyForExport] = useState('');
    const [results, setResults] = useState([]);
    const [simpleSearch, setSimpleSearch] = useState(false);
    const [modified, setModified] = useState(false);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const tableId = "tbl_mat_table";
    const searchId = "txt_mat_search";
    const deletePermission = (DEV || permission?.includes(permissionObj?.WEB_HID_DELETE));
    const leftPosition = deletePermission ? LEFT_FIRST_COLUMN_WITH_CHECKBOX : DEFAULT_LEFT;
    const option = TABLES.HID_ACR;

    /**
     * Builds the table header definition for the ACR listing table.
     *
     * @returns {Array<JSX.Element>} Array of <CustomTh> elements representing column headers.
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} classes="first_head" fixedXY={true} left={leftPosition}>{t('zone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('controller')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('sio')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_reader')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('entry_output')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('keep_open')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('exit_reader')}</CustomTh>
        ];
    };

    /**
     * Builds the table body rows for displaying ACR records.
     *
     * @returns {Object} Object containing the JSX body and raw data for the table.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        const body = results?.content?.map((item) => (
            <>
                <CustomTd tableId={tableId} classes='first-column' fixed={true} left={leftPosition}>
                    <CustomTooltip
                        text={item?.zoneName || EMPTY_VALUES.DEFAULT}
                        classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomTooltip
                        text={item?.hidName || EMPTY_VALUES.DEFAULT}
                        classes={`fs-14px font-color-general-950  text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomTooltip
                        text={item?.sioName || EMPTY_VALUES.DEFAULT}
                        classes={`fs-14px font-color-general-950  text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomTooltip
                        text={item?.entryReaderName || EMPTY_VALUES.DEFAULT}
                        classes={`fs-14px font-color-general-950  text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>
                    <CustomTooltip
                        text={item?.entryOutputName}
                        classes={`fs-14px font-color-general-950  text-ellipsis`}
                        showIfTruncated={true}
                    />
                </CustomTd>
                <CustomTd tableId={tableId}>{item?.keepOpen ? `${item?.keepOpen} ${t('sec')}` : EMPTY_VALUES.DEFAULT}</CustomTd>
                <CustomTd tableId={tableId}>{item?.exitReaderName || EMPTY_VALUES.DEFAULT}</CustomTd>
            </>
        ));

        return {
            body,
            data: results?.content
        };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, key:searchElement });
        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) {
                setResults(data);
            }
        });
    }

    /**
     * Custom hook from TableFunction to manage pagination, selection, and table behavior.
     */
    const {
        t,
        thead,
        tbody,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose
    } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getThead,
        getTbody,
        labelRowsPerPage: 'acrs_per_page',
        setIsAllChecked,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Deletes all currently selected ACR records.
     * After successful deletion, refreshes the table and resets selections.
     */
    const deleteSelectedAcrs = () => {
        const body = selectedList;

        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: option, body })).then(({ payload: { data: { success } } }) => {
                if (success === true) {
                    setSelectedList([]);
                    setIsAllChecked(false);
                    setModified(true);
                }
            });
        }
    };

    /**
     * Triggers the action to remotely open a door associated with the given acr.
     * Dispatches the `OpenTheDoor` thunk using the item's ID and handles the API response.
     *
     * @param {Object} item - Object containing information about the acr.
     * @param {string|number} item.id - The identifier required to perform the open-door action.
     */
    const handleOpenTheDoor = (item) => {
        dispatch(OpenTheDoor(item?.id)).then(({ payload: { data: { success, code } } }) => {
            if (success === true) {
                toast.success(t("has_been_opened_successfully"));
            }else errorMsg(code)
        });
    };

    /**
     * Initiates the linking mode for the specified acr.
     * Dispatches the `LinkingMode` thunk using the item's ID and processes the backend response.
     *
     * @param {Object} item - Object representing the acr to be linked.
     * @param {string|number} item.id - The identifier used to trigger the linking mode action.
     */
    const handleLinkingModel = (item) => {
        dispatch(LinkingMode(item?.id)).then(({ payload: { data: { success, code } } }) => {
            if (success === true) {
                toast.success(t("has_been_linking_successfully"));
            }else errorMsg(code)
        });
    };

    const getActionsButtons = (item, index) => {
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`} // Unique menu ID for each contractor
            >
                <DropDownMenuItem
                    id={'btn_mat_open_door_' + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('open_door')}
                    click={() => {
                        handleOpenTheDoor(item);
                    }}
                    icon={<DetailsIconsOptions />}
                />

                <DropDownMenuItem
                    id={'btn_mat_linking_mode_' + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('linking_mode')}
                    click={() => {
                        handleLinkingModel(item);
                    }}
                />
            </DropDownMenuProfile>
        );
    };

    return {
        t,
        DEV,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        pagination,
        page,
        rowsPerPage,
        setResults,
        option,
        searchElement,
        setSearchElement,
        setSearch,
        setBodyForExport,
        setSimpleSearch,
        deletePermission,
        isAllChecked,
        setIsAllChecked,
        setModified,
        handleCheckboxChange,
        handleDeleteAll,
        deleteSelectedAcrs,
        getActionsButtons
    };
};

export default MainAcrTableApp;
